import QuizInteractiveFlow from "@/components/storyboard/quiz/QuizInteractiveFlow";
import QuizCodeTrueFalse, {
  type QuizCodeTrueFalseState,
} from "@/components/storyboard/quiz/QuizCodeTrueFalse";
import { buildCodeTrueFalseEntryReview } from "@/components/storyboard/quiz/review/buildEntryReview";
import { useQuizValidations } from "@/hooks/useQuizValidations";
import { InteractiveElementStatusKind } from "@/lib/interactiveElementState";
import type { QuizCodeTrueFalseEntryData } from "@/types/storyboard";

export interface QuizCodeTrueFalseFlowProps {
  id: string;
  title: string;
  entries: QuizCodeTrueFalseEntryData[];
  onValidate: (answer: boolean, entryIndex: number) => Promise<boolean>;
}

/** Code True/False quiz in the shared interactive-element shell — inline in the
 * storyboard card. The verdict comes from the server, so a finished or
 * half-finished quiz is restored from `validation_data` on any device.
 */
const QuizCodeTrueFalseFlow = ({
  id,
  title,
  entries,
  onValidate,
}: QuizCodeTrueFalseFlowProps) => {
  const {
    status: statusOverride,
    isReady: validationsReady,
    refresh,
  } = useQuizValidations(
    id,
    entries.length,
    (answeredCount, correctCount): QuizCodeTrueFalseState => ({
      currentIndex: answeredCount,
      selectedAnswer: null,
      isSubmitted: false,
      isCorrect: null,
      correctCount,
    }),
    (validation) =>
      buildCodeTrueFalseEntryReview(
        entries[validation.entryIndex],
        validation.userInput === "true",
        !!validation.result.correct
      )
  );
  const serverResume =
    statusOverride?.kind === InteractiveElementStatusKind.InProgress
      ? (statusOverride.partial as QuizCodeTrueFalseState)
      : undefined;

  return (
    <QuizInteractiveFlow<QuizCodeTrueFalseState>
      id={id}
      title={title}
      illustrationKind="code_true_false"
      layout="inline"
      statusOverride={statusOverride}
      supportsInProgress
      reportProgress
      renderQuiz={({
        initialPartial,
        initialIndex,
        onPartialChange,
        onComplete,
      }) => {
        const seedReady = initialPartial != null || validationsReady;
        if (!seedReady) return null;

        return (
          <QuizCodeTrueFalse
            entries={entries}
            onValidate={async (...args) => {
              const verdict = await onValidate(...args);
              refresh();
              return verdict;
            }}
            initialPartial={initialPartial ?? serverResume}
            initialIndex={initialIndex ?? serverResume?.currentIndex}
            onPartialChange={onPartialChange}
            onComplete={onComplete}
          />
        );
      }}
    />
  );
};

export default QuizCodeTrueFalseFlow;
